import React, { useState } from "react";
import {
  Badge,
  Button,
  Card,
  Col,
  Form,
  InputGroup,
  Modal,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import { useMutation, useQuery } from "@apollo/client";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEye,
  faEyeSlash,
  faPen,
  faPlusCircle,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import Swal from "sweetalert2";

import { COLOR_APP } from "../../../constants";
import { GET_CF_ROLES, GET_SHOP_STAFFS } from "../../../graphql/staff/query";
import {
  CREATE_SHOP_STAFF,
  DELETE_SHOP_STAFF,
  ON_OFF_SHOP_STAFF,
  UPDATE_SHOP_STAFF,
} from "../../../graphql/staff/mutation";
import { errorSwal, successSwal } from "../../../helpers/sweetalert";
import LoadingSpinner from "../../../components/LoadingSpinner";

const MIN_PASSWORD_LENGTH = 6;

//* Only CF staff (role STAFF + platform CF) are staff; older shop logins with role STAFF are admins.
const isAdmin = (user) => !(user?.role === "STAFF" && user?.platform === "CF");

const EMPTY_FORM = {
  id: null,
  username: "",
  password: "",
  firstName: "",
  lastName: "",
  nickname: "",
  phone: "",
  gender: "MALE",
  permission: "",
};

export default function SettingsStaff() {
  const [form, setForm] = useState(EMPTY_FORM);
  const [isOpen, setIsOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");

  const { data, loading, refetch } = useQuery(GET_SHOP_STAFFS, {
    variables: {
      where: {
        search: search || undefined,
        permission: roleFilter || undefined,
      },
      limit: 200,
    },
    fetchPolicy: "network-only",
  });
  const { data: roleData } = useQuery(GET_CF_ROLES, {
    fetchPolicy: "network-only",
  });
  const [createStaff, { loading: creating }] = useMutation(CREATE_SHOP_STAFF);
  const [updateStaff, { loading: updating }] = useMutation(UPDATE_SHOP_STAFF);
  const [onOffStaff] = useMutation(ON_OFF_SHOP_STAFF);
  const [deleteStaff] = useMutation(DELETE_SHOP_STAFF);

  //* Admin accounts come back too (read-only); list them first.
  const staffs = [...(data?.shopStaffs?.dataMany?.data ?? [])].sort(
    (a, b) => isAdmin(b) - isAdmin(a),
  );
  const roles = roleData?.cfRoles?.dataMany ?? [];
  const saving = creating || updating;
  const isEdit = !!form.id;

  const openForm = (staff) => {
    setShowPassword(false);
    setForm(
      staff
        ? {
            ...EMPTY_FORM,
            id: staff.id,
            username: staff.username ?? "",
            firstName: staff.firstName ?? "",
            lastName: staff.lastName ?? "",
            nickname: staff.nickname ?? "",
            phone: staff.phone ?? "",
            gender: staff.gender ?? "MALE",
            permission: staff.permission?.id ?? "",
          }
        : EMPTY_FORM,
    );
    setIsOpen(true);
  };

  const closeForm = () => {
    setIsOpen(false);
    setForm(EMPTY_FORM);
  };

  const validate = () => {
    if (!isEdit && !form.username.trim()) return "ກະລຸນາປ້ອນຊື່ຜູ້ໃຊ້";
    if (!form.firstName.trim()) return "ກະລຸນາປ້ອນຊື່";
    if (!/^\d{8}$/.test(form.phone.trim()))
      return "ເບີໂທຕ້ອງເປັນຕົວເລກ 8 ຫຼັກ (ເຊັ່ນ 20xxxxxx)";
    if (!form.permission) return "ກະລຸນາເລືອກບົດບາດ";
    if (!isEdit && form.password.length < MIN_PASSWORD_LENGTH)
      return `ລະຫັດຜ່ານຕ້ອງມີຢ່າງໜ້ອຍ ${MIN_PASSWORD_LENGTH} ຕົວ`;
    if (isEdit && form.password && form.password.length < MIN_PASSWORD_LENGTH)
      return `ລະຫັດຜ່ານຕ້ອງມີຢ່າງໜ້ອຍ ${MIN_PASSWORD_LENGTH} ຕົວ`;
    return null;
  };

  const handleSave = async () => {
    if (saving) return;
    const error = validate();
    if (error) return errorSwal(error, "warning");

    const payload = {
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      nickname: form.nickname.trim(),
      phone: form.phone.trim(),
      gender: form.gender,
      permission: form.permission,
      ...(form.password ? { password: form.password } : {}),
    };
    try {
      const res = isEdit
        ? (await updateStaff({ variables: { id: form.id, data: payload } }))
            ?.data?.updateShopStaff
        : (
            await createStaff({
              variables: {
                data: { ...payload, username: form.username.trim() },
              },
            })
          )?.data?.createShopStaff;
      if (!res?.status) return errorSwal(res?.msg);
      successSwal(isEdit ? "ແກ້ໄຂສຳເລັດ" : "ເພີ່ມພະນັກງານສຳເລັດ");
      closeForm();
      refetch();
    } catch (err) {
      errorSwal();
    }
  };

  const handleOnOff = async (staff) => {
    try {
      const res = (
        await onOffStaff({
          variables: { id: staff.id, isDeleted: !staff.isDeleted },
        })
      )?.data?.onOffShopStaff;
      if (!res?.status) return errorSwal(res?.msg);
      refetch();
    } catch (err) {
      errorSwal();
    }
  };

  const handleDelete = async (staff) => {
    const confirm = await Swal.fire({
      icon: "warning",
      title: `ລົບພະນັກງານ "${staff.firstName ?? staff.username}"?`,
      text: "ບັນຊີນີ້ຈະບໍ່ສາມາດເຂົ້າສູ່ລະບົບໄດ້ອີກ",
      showCancelButton: true,
      confirmButtonText: "ລົບ",
      cancelButtonText: "ຍົກເລີກ",
      confirmButtonColor: COLOR_APP,
    });
    if (!confirm.isConfirmed) return;
    try {
      const res = (await deleteStaff({ variables: { id: staff.id } }))?.data
        ?.deleteShopStaff;
      if (!res?.status) return errorSwal(res?.msg);
      successSwal("ລົບສຳເລັດ");
      refetch();
    } catch (err) {
      errorSwal();
    }
  };

  const setField = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  return (
    <div>
      <div
        className="d-flex justify-content-between align-items-center flex-wrap"
        style={{ gap: 8 }}
      >
        <h5 style={{ fontWeight: "bold", margin: 0 }}>
          ພະນັກງານ ({data?.shopStaffs?.dataMany?.total ?? 0})
        </h5>
        <div className="d-flex" style={{ gap: 8 }}>
          <Button
            style={{ background: COLOR_APP, border: "none" }}
            onClick={() => {
              if (roles.length === 0)
                return errorSwal(
                  'ກະລຸນາສ້າງບົດບາດໃນແທັບ "ບົດບາດ ແລະ ສິດ" ກ່ອນ ຈຶ່ງເພີ່ມພະນັກງານໄດ້',
                  "warning",
                );
              openForm(null);
            }}
          >
            <FontAwesomeIcon icon={faPlusCircle} /> ເພີ່ມພະນັກງານ
          </Button>
        </div>
      </div>

      <Row className="mt-3" style={{ rowGap: 8 }}>
        <Col md={4}>
          <Form.Control
            placeholder="ຄົ້ນຫາ ຊື່, ຊື່ຜູ້ໃຊ້, ເບີໂທ"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </Col>
        <Col md={3}>
          <Form.Select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
          >
            <option value="">ທຸກບົດບາດ</option>
            {roles.map((role) => (
              <option key={role.id} value={role.id}>
                {role.name}
              </option>
            ))}
          </Form.Select>
        </Col>
      </Row>

      <Card style={{ marginTop: 16 }}>
        <Card.Body>
          {loading ? (
            <LoadingSpinner />
          ) : (
            <Table responsive hover>
              <thead>
                <tr>
                  <th>#</th>
                  <th>ຊື່ ແລະ ນາມສະກຸນ</th>
                  <th>ຊື່ຜູ້ໃຊ້</th>
                  <th>ເບີໂທ</th>
                  <th>ບົດບາດ</th>
                  <th>ສະຖານະ</th>
                  <th style={{ textAlign: "right" }}>ຈັດການ</th>
                </tr>
              </thead>
              <tbody>
                {staffs.length === 0 && (
                  <tr>
                    <td
                      colSpan={7}
                      style={{ textAlign: "center", color: "#999" }}
                    >
                      ຍັງບໍ່ມີພະນັກງານ
                    </td>
                  </tr>
                )}
                {staffs.map((staff, index) => (
                  <tr
                    key={staff.id}
                    style={{ opacity: staff.isDeleted ? 0.55 : 1 }}
                  >
                    <td>{index + 1}</td>
                    <td>
                      {[staff.firstName, staff.lastName]
                        .filter(Boolean)
                        .join(" ") || "-"}
                      {staff.nickname ? ` (${staff.nickname})` : ""}
                    </td>
                    <td>{staff.username}</td>
                    <td>{staff.phone || "-"}</td>
                    <td>
                      {isAdmin(staff) ? (
                        <Badge bg="danger">ແອັດມິນ</Badge>
                      ) : (
                        (staff.permission?.name ?? (
                          <span style={{ color: "#c00" }}>ບໍ່ມີບົດບາດ</span>
                        ))
                      )}
                    </td>
                    <td>
                      {isAdmin(staff) ? (
                        <Badge bg="success">ເປີດ</Badge>
                      ) : (
                        <Form.Check
                          type="switch"
                          id={`staff-switch-${staff.id}`}
                          checked={!staff.isDeleted}
                          onChange={() => handleOnOff(staff)}
                          label={
                            staff.isDeleted ? (
                              <Badge bg="secondary">ປິດ</Badge>
                            ) : (
                              <Badge bg="success">ເປີດ</Badge>
                            )
                          }
                        />
                      )}
                    </td>
                    <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                      {isAdmin(staff) ? (
                        <span style={{ color: "#999" }}>-</span>
                      ) : (
                        <>
                          <Button
                            size="sm"
                            variant="warning"
                            onClick={() => openForm(staff)}
                          >
                            <FontAwesomeIcon icon={faPen} />
                          </Button>{" "}
                          <Button
                            size="sm"
                            variant="danger"
                            onClick={() => handleDelete(staff)}
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </Button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <Modal show={isOpen} onHide={closeForm} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontWeight: "bold" }}>
            {isEdit ? "ແກ້ໄຂຂໍ້ມູນພະນັກງານ" : "ເພີ່ມພະນັກງານ"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>ຊື່ *</Form.Label>
                <Form.Control
                  value={form.firstName}
                  onChange={setField("firstName")}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>ນາມສະກຸນ</Form.Label>
                <Form.Control
                  value={form.lastName}
                  onChange={setField("lastName")}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>ຊື່ຫຼິ້ນ</Form.Label>
                <Form.Control
                  value={form.nickname}
                  onChange={setField("nickname")}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>ເພດ</Form.Label>
                <Form.Select value={form.gender} onChange={setField("gender")}>
                  <option value="MALE">ຊາຍ</option>
                  <option value="FEMALE">ຍິງ</option>
                  <option value="OTHER">ອື່ນໆ</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>ເບີໂທ 8 ໂຕ *</Form.Label>
                <Form.Control
                  value={form.phone}
                  maxLength={8}
                  placeholder="xxxx xxxx"
                  onChange={(e) =>
                    setForm({
                      ...form,
                      phone: e.target.value.replace(/\D/g, ""),
                    })
                  }
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>ບົດບາດ *</Form.Label>
                <Form.Select
                  value={form.permission}
                  onChange={setField("permission")}
                >
                  <option value="">-- ເລືອກບົດບາດ --</option>
                  {roles.map((role) => (
                    <option key={role.id} value={role.id}>
                      {role.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>ຊື່ຜູ້ໃຊ້ (ໃຊ້ເຂົ້າສູ່ລະບົບ) *</Form.Label>
                <Form.Control
                  value={form.username}
                  disabled={isEdit}
                  autoComplete="off"
                  onChange={(e) =>
                    setForm({
                      ...form,
                      username: e.target.value.replace(/\s/g, ""),
                    })
                  }
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  {isEdit
                    ? "ລະຫັດຜ່ານໃໝ່ (ປ່ອຍວ່າງ ຖ້າບໍ່ປ່ຽນ)"
                    : "ລະຫັດຜ່ານ *"}
                </Form.Label>
                <InputGroup>
                  <Form.Control
                    type={showPassword ? "text" : "password"}
                    value={form.password}
                    autoComplete="new-password"
                    onChange={setField("password")}
                  />
                  <Button
                    variant="outline-secondary"
                    onClick={() => setShowPassword((v) => !v)}
                  >
                    <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                  </Button>
                </InputGroup>
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={closeForm}>
            ຍົກເລີກ
          </Button>
          <Button
            style={{ background: COLOR_APP, border: "none" }}
            disabled={saving}
            onClick={handleSave}
          >
            {saving && <Spinner size="sm" />}{" "}
            {isEdit ? "ຢືນຢັນແກ້ໄຂ" : "ຢືນຢັນ"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
